/*
  Maple "Complete your build" row (snippets/maple-pairs.liquid, 23 Oct): the "Add" button of a pair with one
  variant. Loaded as a module by the row itself, and once with the page by snippets/cart-drawer.liquid and
  sections/main-cart.liquid, because a row that arrives later in a section-rendered drawer or cart page
  brings its script tag along but the browser does not run it. Same URL everywhere, so it runs once.

  One delegated click listener (rows are re-rendered by Horizon's cart morph, so nothing is bound to them).
  An add follows Horizon's product form (assets/product-form.js, #processAddToCart):
  - POST {routes.cart_add_url} (/cart/add.js) with the variant, quantity 1, the hidden line item property
    _added_from = pairs, and `sections` = the data-section-id of every <cart-items-component> on the page
    (the drawer's "cart-drawer-section", or the cart page's section), exactly what the product form asks for;
  - on success, the cart is read the way the product form reads it (cart-items-component.fetchCartData(),
    /cart.json without one), then the same CartLinesUpdateEvent (@shopify/events) is dispatched, its promise
    resolved with the cart and the add response's `sections`. Horizon's own listeners do the rest:
    cart-items-component morphs the drawer / cart page from those sections (no second section fetch),
    cart-icon updates the bubble, header-actions announces the new count in its live region
    (role="status", "Total items in cart: N"), product forms resync their quantity, and cart-drawer
    auto-opens the drawer when the theme's "auto open" setting is on;
  - the event is dispatched after the add answers (Horizon dispatches before): an add that fails then
    dispatches only Horizon's CartErrorEvent, so no cart refresh re-renders the row and wipes the error
    shown on the card (the cart's error text from the response, else the theme's "Error adding to cart").
  The button is disabled while the request runs. When the button is gone after the re-render (the pair is
  now in the cart, so the drawer and cart page drop it) and focus fell to the page, focus moves to the new
  line item's title link, else to the row heading, else to the drawer heading.
*/
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADD_SELECTOR = 'button[data-maple-pairs-add]';
const SOURCE = 'maple-pairs';

/** @returns {Promise<any>} the cart, read as Horizon's product form reads it */
async function readCart() {
  const component = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (component) {
    await customElements.whenDefined('cart-items-component');
    if (typeof component.fetchCartData === 'function') return component.fetchCartData();
  }

  const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/** @returns {string[]} the section ids Horizon's product form sends with an add */
function cartSectionIds() {
  const ids = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
  }
  return ids;
}

/**
 * @param {HTMLElement | null} error
 * @param {string} message
 */
function showError(error, message) {
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

/** @param {HTMLElement | null} error */
function clearError(error) {
  if (!error) return;
  error.hidden = true;
  error.textContent = '';
}

/** @param {HTMLElement | null} element */
function focusQuietly(element) {
  if (!element) return false;
  if (!element.matches('a[href], button, input, select, textarea, [tabindex]')) element.setAttribute('tabindex', '-1');
  element.focus();
  return document.activeElement === element;
}

/**
 * After the cart re-render: when the clicked button is gone and focus fell to the page, put it somewhere useful.
 * @param {HTMLButtonElement} button
 * @param {string} headingId
 * @param {string} lineKey
 */
function settleFocus(button, headingId, lineKey) {
  let frames = 0;
  const check = () => {
    if (button.isConnected) {
      if (frames++ < 120) requestAnimationFrame(check);
      return;
    }
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;

    const line = lineKey ? document.getElementById(`CartItem-${lineKey}`) : null;
    const lineLink = /** @type {HTMLElement | null} */ (line?.querySelector('a.cart-items__title') ?? null);
    if (focusQuietly(lineLink)) return;
    if (focusQuietly(headingId ? document.getElementById(headingId) : null)) return;
    focusQuietly(document.getElementById('cart-drawer-heading'));
  };
  requestAnimationFrame(check);
}

/** @param {HTMLButtonElement} button */
async function addPair(button) {
  if (button.disabled) return;

  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  const row = /** @type {HTMLElement | null} */ (button.closest('[data-maple-pairs]'));
  const item = button.closest('.maple-pairs__item');
  const error = /** @type {HTMLElement | null} */ (item?.querySelector('[data-maple-pairs-error]') ?? null);
  const fallbackError = row?.dataset.errorText || 'Error adding to cart';
  const context = row?.dataset.context === 'product' ? 'product' : 'cart';
  const headingId = row?.getAttribute('aria-labelledby') ?? '';
  const hadFocus = document.activeElement === button;

  clearError(error);
  button.disabled = true;

  /** @type {Record<string, any>} */
  const body = {
    items: [{ id: variantId, quantity: 1, properties: { _added_from: 'pairs' } }],
  };
  const sectionIds = cartSectionIds();
  if (sectionIds.length) body.sections = sectionIds.join(',');

  let lineKey = '';
  let added = false;

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (data.status || !response.ok) {
      // /cart/add.js errors: { status, message, description }
      showError(error, data.description || data.message || fallbackError);
      button.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
      return;
    }

    added = true;
    lineKey = data.items?.[0]?.key ?? data.key ?? '';

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cart = await readCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: SOURCE,
          itemCount: 1,
          productId: button.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (cartError) {
      // As Horizon's product form: the add went through, only the cart read failed
      deferred.reject(cartError);
    }
  } catch (requestError) {
    console.error(requestError);
    showError(error, fallbackError);
    button.dispatchEvent(
      new CartErrorEvent({
        error: requestError instanceof Error ? requestError.message : 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    button.disabled = false;
    if (hadFocus && button.isConnected && (!document.activeElement || document.activeElement === document.body)) {
      button.focus();
    }
  }

  if (added && hadFocus) settleFocus(button, headingId, lineKey);
}

/** @param {MouseEvent} event */
function onClick(event) {
  const button = event.target instanceof Element ? event.target.closest(ADD_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;
  event.preventDefault();
  addPair(button);
}

if (!(/** @type {any} */ (window).maplePairsReady)) {
  /** @type {any} */ (window).maplePairsReady = true;
  document.addEventListener('click', onClick);
}
